import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addTicketPayment,
  closeTicket,
  deleteTicketPayment,
  getTicketById,
  listTicketPayments,
  saveTicketPayments,
  setTicketTips,
  updateTicket,
} from "../api/tickets";
import { listStaff } from "../api/staffs";
import { validateGiftCard } from "../api/giftCards";
import {
  getClientRewards,
  getRewardsSettings,
  redeemTicketRewards,
} from "../api/rewards";
import { didTicketJustClose } from "../utils/ticketStatusTransitions";
import SBINPageCard from "../components/layout/SBINPageCard";
import ModalCloseX from "../components/common/ModalCloseX";
import "./TicketCollectPayment.css";

const APPOINTMENTS_VERSION_STORAGE_KEY = "sbin:appointmentsVersion";

const PAYMENT_METHODS = [
  { key: "cash", label: "Cash" },
  {
    key: "debit",
    label: "Debit",
    hover: "Interac Card, Bank Card, ATM Card, Check Card",
  },
  {
    key: "credit",
    label: "Credit",
    hover: "VISA, MASTERCARD, AMEX, DISCOVER, etc.",
  },
  { key: "gift_card", label: "Gift Card #", referenceLabel: "Gift Card #" },
  { key: "house", label: "House" },
];

const buildEmptyPayments = () =>
  PAYMENT_METHODS.reduce((acc, method) => {
    if (method.key !== "gift_card") {
      acc[method.key] = { amount: "", reference: "" };
    }
    return acc;
  }, {});

const formatAmountInput = (amount) => {
  const num = Number(amount || 0);
  if (!Number.isFinite(num) || num <= 0) return "";
  return num.toFixed(2);
};

function formatMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "$0.00";
  return `$${num.toFixed(2)}`;
}

function parseAmount(value) {
  const num = Number(value || 0);
  return Number.isFinite(num) ? num : 0;
}

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function round2(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function fmt2(value) {
  return round2(value).toFixed(2);
}

function toCents(value) {
  return Math.round((Number(value) || 0) * 100);
}

export default function TicketCollectPayment() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const ticketIdValue = Number(ticketId || 0) || null;

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [banner, setBanner] = useState("");
  const [ticketNote, setTicketNote] = useState("");
  const [showTipModal, setShowTipModal] = useState(false);
  const [tipRows, setTipRows] = useState([]);
  const [tipEmployeeId, setTipEmployeeId] = useState("");
  const [tipAmountInput, setTipAmountInput] = useState("");
  const [tipPercentInput, setTipPercentInput] = useState("");
  const [tipError, setTipError] = useState("");
  const [tipSaving, setTipSaving] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [payments, setPayments] = useState(() => buildEmptyPayments());
  const [loadedPayments, setLoadedPayments] = useState([]);
  const [allPaymentRows, setAllPaymentRows] = useState([]);
  const [finalizedGiftCards, setFinalizedGiftCards] = useState([]);
  const [giftCardEntry, setGiftCardEntry] = useState({
    code: "",
    status: "idle",
    remainingBalance: 0,
    appliedAmount: "",
  });
  const [giftCardEntryWarning, setGiftCardEntryWarning] = useState("");
  const [giftCardEntryError, setGiftCardEntryError] = useState("");
  const amountInputRefs = useRef({});
  const [autoFilledKey, setAutoFilledKey] = useState("");
  const [rewardsSettings, setRewardsSettings] = useState(null);
  const [rewardsAccount, setRewardsAccount] = useState(null);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [rewardsError, setRewardsError] = useState("");
  const [redeemPoints, setRedeemPoints] = useState("");
  const [redeemSaving, setRedeemSaving] = useState(false);
  const [redeemNotice, setRedeemNotice] = useState("");
  const [redeemError, setRedeemError] = useState("");
  const emitScheduleRevenueRefresh = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        APPOINTMENTS_VERSION_STORAGE_KEY,
        String(Date.now())
      );
    } catch (_) {
      // Ignore localStorage write failures and still emit the event.
    }
    window.dispatchEvent(new CustomEvent("sbin:appointmentsChanged"));
  }, []);

  const fetchTicket = async () => {
    if (!ticketId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getTicketById(ticketId);
      setTicket(data);
      setTicketNote(data?.notes || "");
      setBanner("");
    } catch (err) {
      console.error(err);
      const statusCode = err?.response?.status;
      if (statusCode === 404) {
        setError(
          "This ticket no longer exists (it may have been removed because the appointment was deleted)."
        );
      } else {
        setError(err?.message || "Unable to load ticket");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const type = (ticket?.ticket_type || "").toLowerCase();
    if (type.startsWith("refund")) {
      navigate(`/tickets/${ticketId}`);
    }
  }, [navigate, ticket, ticketId]);

  const fetchSavedPayments = useCallback(async () => {
    if (!ticketIdValue) return;
    try {
      const rows = await listTicketPayments(ticketIdValue);
      const normalizedRows = Array.isArray(rows) ? rows : [];
      setAllPaymentRows(normalizedRows);
      const activeRows = normalizedRows.filter(
        (row) => row?.is_applied !== false
      );
      const nextPayments = buildEmptyPayments();
      activeRows.forEach((payment) => {
        const method = (payment.method || "").toLowerCase();
        if (!method || method === "gift_card" || method === "check") return;
        nextPayments[method] = {
          amount: formatAmountInput(payment.amount),
          reference: payment.reference || "",
        };
      });
      setPayments(nextPayments);
      setLoadedPayments(activeRows);

      const giftCards = activeRows
        .filter((payment) => (payment.method || "").toLowerCase() === "gift_card")
        .map((payment) => ({
          id: payment.id,
          code: payment.reference || "",
          remainingBalance: 0,
          appliedAmount: Number(payment.amount || 0),
          is_pending: Boolean(payment.is_pending),
        }));
      setFinalizedGiftCards(giftCards);
    } catch (err) {
      console.error(err);
      setPayments(buildEmptyPayments());
      setFinalizedGiftCards([]);
      setLoadedPayments([]);
      setAllPaymentRows([]);
    }
  }, [ticketIdValue]);

  const loadRewardsData = useCallback(async () => {
    if (!ticket?.client_id) {
      setRewardsAccount(null);
      setRewardsSettings(null);
      return;
    }
    setRewardsLoading(true);
    setRewardsError("");
    try {
      const [settings, account] = await Promise.all([
        getRewardsSettings(),
        getClientRewards(ticket.client_id),
      ]);
      setRewardsSettings(settings);
      setRewardsAccount(account);
    } catch (err) {
      setRewardsSettings(null);
      setRewardsAccount(null);
      setRewardsError(
        err?.response?.data?.detail || "Unable to load rewards balance."
      );
    } finally {
      setRewardsLoading(false);
    }
  }, [ticket?.client_id]);

  useEffect(() => {
    fetchTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  useEffect(() => {
    fetchSavedPayments();
  }, [fetchSavedPayments]);

  useEffect(() => {
    loadRewardsData();
  }, [loadRewardsData]);

  useEffect(() => {
    if (!showTipModal || !ticket?.salon_id) return;
    setTipError("");
    setTipAmountInput("");
    setTipPercentInput("");
    setTipRows(
      (ticket?.tips || []).map((tip) => ({
        staff_id: tip.staff_id,
        staff_name:
          tip.staff?.display_name ||
          tip.staff?.full_name ||
          `Staff #${tip.staff_id}`,
        amount: Number(tip.amount || 0),
      }))
    );
    setStaffLoading(true);
    listStaff({ salon_id: ticket.salon_id, is_active: true })
      .then((data) => {
        setStaffOptions(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error(err);
        setStaffOptions([]);
      })
      .finally(() => {
        setStaffLoading(false);
      });
  }, [showTipModal, ticket]);

  useEffect(() => {
    if (!showTipModal) return;
    if (staffOptions.length === 0) return;
    if (!tipEmployeeId) {
      setTipEmployeeId(String(staffOptions[0].id));
    }
  }, [showTipModal, staffOptions, tipEmployeeId]);

  const clientName =
    ticket?.client?.display_name ||
    ticket?.client?.full_name ||
    ticket?.client?.name ||
    "Walk-in";
  const isClosed = ticket?.status === "closed";
  const rewardsEnabled = rewardsSettings?.enabled ?? false;
  const pointValueCents = Number(rewardsSettings?.point_value_cents ?? 1) || 1;
  const rewardsBalancePoints = Number(rewardsAccount?.points_balance ?? 0) || 0;
  const rewardsBalanceValue =
    (rewardsBalancePoints * pointValueCents) / 100;
  const redeemPointsValue =
    (Number(redeemPoints || 0) * pointValueCents) / 100;
  const rewardPayments = useMemo(
    () =>
      (allPaymentRows || []).filter(
        (row) => (row?.method || "").toLowerCase() === "rewards_points"
      ),
    [allPaymentRows]
  );
  const headerActions = (
    <button
      type="button"
      className="rounded-full border border-white/40 bg-white/10 px-4 py-2 text-sm md:text-base font-semibold text-white transition hover:bg-white/20"
      onClick={() => navigate(`/tickets/${ticketId}`)}
    >
      Back to Ticket
    </button>
  );

  const parseMoney = useCallback((value) => {
    if (value === "" || value === null || value === undefined) return 0;
    const num = Number(value);
    if (!Number.isFinite(num)) return 0;
    return round2(num);
  }, []);

  const getGiftCardEnteredTotal = useCallback(() => {
    const finalizedTotal = finalizedGiftCards.reduce((sum, row) => {
      if (!row?.code) return sum;
      return sum + parseMoney(row.appliedAmount);
    }, 0);
    const entryTotal =
      giftCardEntry.status === "valid" && giftCardEntry.code
        ? parseMoney(giftCardEntry.appliedAmount)
        : 0;
    return round2(finalizedTotal + entryTotal);
  }, [finalizedGiftCards, giftCardEntry, parseMoney]);

  const getStandardEnteredTotal = useCallback(() => {
    return PAYMENT_METHODS.reduce((sum, method) => {
      if (method.key === "gift_card") return sum;
      const value = payments[method.key]?.amount;
      return sum + parseMoney(value);
    }, 0);
  }, [payments, parseMoney]);

  const isTicketClosed = (ticket?.status || "").toLowerCase() === "closed";
  const previousCloseRows = useMemo(() => {
    const cv = Number(ticket?.close_version || 0);
    if (!cv) return [];
    return (allPaymentRows || []).filter((row) => {
      if (row?.is_applied !== false) return false;
      return Number(row?.close_version || 0) === cv;
    });
  }, [allPaymentRows, ticket?.close_version]);
  const previousByMethod = useMemo(() => {
    const map = {};
    previousCloseRows.forEach((row) => {
      const method = String(row?.method || "").toLowerCase();
      if (!method || method === "gift_card" || method === "check") return;
      map[method] = {
        amount: Number(row?.amount || 0),
        reference: row?.reference || "",
      };
    });
    return map;
  }, [previousCloseRows]);
  const previousGiftCards = useMemo(() => {
    return previousCloseRows
      .filter((row) => String(row?.method || "").toLowerCase() === "gift_card")
      .map((row) => ({
        code: row?.reference || "",
        amount: Number(row?.amount || 0),
      }));
  }, [previousCloseRows]);
  const editableMethodKeys = useMemo(
    () => new Set(PAYMENT_METHODS.map((method) => method.key)),
    []
  );
  const previousPaid = useMemo(() => {
    return loadedPayments.reduce((sum, row) => {
      const method = String(row?.method || "").toLowerCase();
      if (!method || editableMethodKeys.has(method)) return sum;
      return sum + parseMoney(row?.amount);
    }, 0);
  }, [loadedPayments, editableMethodKeys, parseMoney]);
  const itemsTotal = parseMoney(ticket?.grand_total || 0);
  const tipsTotal = parseMoney(ticket?.total_tips || 0);
  const grandTotal = round2(itemsTotal + tipsTotal);
  const standardEnteredTotal = useMemo(
    () => getStandardEnteredTotal(),
    [getStandardEnteredTotal]
  );
  const finalizedGiftCardTotal = useMemo(
    () =>
      finalizedGiftCards.reduce(
        (sum, row) => sum + parseMoney(row.appliedAmount),
        0
      ),
    [finalizedGiftCards, parseMoney]
  );
  const entryGiftCardAmount =
    giftCardEntry.status === "valid" && giftCardEntry.code
      ? parseMoney(giftCardEntry.appliedAmount)
      : 0;
  const giftCardEnteredTotal = useMemo(
    () => getGiftCardEnteredTotal(),
    [getGiftCardEnteredTotal]
  );
  const totalPaid = round2(
    previousPaid + standardEnteredTotal + giftCardEnteredTotal
  );
  const totalEnteredPayment = round2(
    standardEnteredTotal + giftCardEnteredTotal
  );
  const amountDueBeforeEntry = Math.max(
    round2(grandTotal - previousPaid - standardEnteredTotal - finalizedGiftCardTotal),
    0
  );
  const amountDuePreview = Math.max(round2(grandTotal - totalPaid), 0);
  const changeDuePreview = Math.max(round2(totalPaid - grandTotal), 0);
  const amountDueCents = toCents(amountDuePreview);
  const amountPaidCents = toCents(totalEnteredPayment);
  const amountPaidDisplay = isTicketClosed
    ? Number(ticket?.amount_paid || 0)
    : totalPaid;
  const amountDueDisplay = isTicketClosed
    ? Math.max(Number(ticket?.balance_due || 0), 0)
    : amountDuePreview;
  const changeDueDisplay = isTicketClosed
    ? Math.max(-Number(ticket?.balance_due || 0), 0)
    : changeDuePreview;

  const serviceTotals = useMemo(() => {
    const totals = new Map();
    for (const item of ticket?.items || []) {
      if ((item.line_type || "").toLowerCase() !== "service") continue;
      const staffId = item.staff?.id ?? item.staff_id;
      if (!staffId) continue;
      const staffName =
        item.staff?.display_name || item.staff?.full_name || `Staff #${staffId}`;
      const lineSubtotal =
        item.line_subtotal ??
        (Number(item.unit_price || 0) * Number(item.quantity || 1) -
          Number(item.line_discount || 0));
      const amount = Number(lineSubtotal || 0);
      totals.set(staffId, {
        staffId,
        staffName,
        amount: (totals.get(staffId)?.amount || 0) + amount,
      });
    }
    return Array.from(totals.values());
  }, [ticket]);

  const totalServiceAmount = useMemo(() => {
    return serviceTotals.reduce((sum, row) => sum + row.amount, 0);
  }, [serviceTotals]);

  const selectedStaffTotal = useMemo(() => {
    const staffId = Number(tipEmployeeId || 0);
    const row = serviceTotals.find((entry) => entry.staffId === staffId);
    if (row && row.amount > 0) return row.amount;
    return totalServiceAmount;
  }, [serviceTotals, tipEmployeeId, totalServiceAmount]);

  const modalBase = "text-base md:text-lg";
  const modalTitle = "text-xl md:text-2xl font-semibold";
  const labelCls =
    "text-sm md:text-base font-semibold tracking-wide text-slate-500 uppercase";
  const fieldCls =
    "h-11 md:h-12 px-4 text-base md:text-lg rounded-xl border border-slate-300 focus:border-rose-500 focus:outline-none";
  const btnCls = "h-11 md:h-12 px-6 md:px-7 text-base md:text-lg rounded-full";

  const handlePaymentChange = (key, field, value) => {
    setPayments((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  };

  const resetGiftCardEntry = () => {
    setGiftCardEntry({
      code: "",
      status: "idle",
      remainingBalance: 0,
      appliedAmount: "",
    });
    setGiftCardEntryWarning("");
    setGiftCardEntryError("");
  };

  const getUsedBalanceForCode = (code) => {
    if (ticketIdValue) return 0;
    const normalized = (code || "").trim().toLowerCase();
    if (!normalized) return 0;
    return finalizedGiftCards.reduce((sum, row) => {
      if (row.code.toLowerCase() !== normalized) return sum;
      return sum + parseAmount(row.appliedAmount);
    }, 0);
  };

  const getAvailableBalanceForCode = (code, remainingBalance) => {
    const used = getUsedBalanceForCode(code);
    return Math.max(0, Number(remainingBalance || 0) - used);
  };

  const getAvailableBalanceForEntry = () =>
    getAvailableBalanceForCode(
      giftCardEntry.code,
      giftCardEntry.remainingBalance
    );

  const handleGiftCardEntryChange = (field, value) => {
    if (field === "code") {
      setGiftCardEntry({
        code: value,
        status: "idle",
        remainingBalance: 0,
        appliedAmount: "",
      });
      setGiftCardEntryWarning("");
      setGiftCardEntryError("");
      return;
    }
    if (field === "appliedAmount") {
      if (giftCardEntry.status !== "valid") {
        setGiftCardEntry((prev) => ({ ...prev, appliedAmount: "" }));
        setGiftCardEntryError("Validate gift card before applying payment.");
        return;
      }
      const parsed = parseAmount(value);
      const availableBalance = getAvailableBalanceForEntry();
      const maxAllowed = Math.max(0, Math.min(availableBalance, amountDueBeforeEntry));
      const clamped = Math.min(Math.max(parsed, 0), maxAllowed);
      if (parsed > maxAllowed) {
        setGiftCardEntryWarning(`Max ${formatMoney(maxAllowed)}`);
      } else {
        setGiftCardEntryWarning("");
      }
      setGiftCardEntry((prev) => ({
        ...prev,
        appliedAmount: value === "" ? "" : parsed > maxAllowed ? clamped.toFixed(2) : value,
      }));
      setGiftCardEntryError("");
    }
  };

  const getRemainingDueExcluding = useCallback(
    (excludeKey) => {
      const paidOther = PAYMENT_METHODS.filter(
        (method) => method.key !== "gift_card" && method.key !== excludeKey
      ).reduce(
        (sum, method) => sum + parseMoney(payments?.[method.key]?.amount),
        0
      );
      return round2(grandTotal - previousPaid - paidOther - giftCardEnteredTotal);
    },
    [giftCardEnteredTotal, grandTotal, parseMoney, payments, previousPaid]
  );

  const handleAmountFocus = (methodKey) => {
    if (methodKey === "gift_card") return;
    const current = toNumber(payments?.[methodKey]?.amount);
    if (current > 0) return;
    const remaining = getRemainingDueExcluding(methodKey);
    if (remaining <= 0) return;
    setPayments((prev) => ({
      ...prev,
      [methodKey]: {
        ...(prev?.[methodKey] || {}),
        amount: fmt2(remaining),
      },
    }));
    setAutoFilledKey(methodKey);
    requestAnimationFrame(() => {
      const el = amountInputRefs.current[methodKey];
      if (el && typeof el.select === "function") {
        el.select();
      }
    });
  };

  const handleValidateGiftCard = async () => {
    const code = (giftCardEntry.code || "").trim();
    if (!code) {
      setGiftCardEntry((prev) => ({
        ...prev,
        status: "invalid",
        remainingBalance: 0,
        appliedAmount: "",
      }));
      setGiftCardEntryWarning("");
      setGiftCardEntryError("");
      return;
    }
    setGiftCardEntry((prev) => ({
      ...prev,
      status: "loading",
    }));
    setGiftCardEntryWarning("");
    setGiftCardEntryError("");
    try {
      const data = await validateGiftCard(
        code,
        ticket?.salon_id || null,
        ticketIdValue
      );
      if (data?.is_valid) {
        const balance = Number(data.remaining || 0);
        const used = getUsedBalanceForCode(code);
        const availableBalance = Math.max(0, balance - used);
        const maxGift = Math.max(
          0,
          Math.min(availableBalance, amountDueBeforeEntry)
        );
        setGiftCardEntry((prev) => ({
          ...prev,
          status: "valid",
          remainingBalance: balance,
          appliedAmount: maxGift > 0 ? maxGift.toFixed(2) : "",
        }));
        setGiftCardEntryError("");
        if (availableBalance <= 0) {
          setGiftCardEntryWarning("No balance available");
        }
      } else {
        setGiftCardEntry((prev) => ({
          ...prev,
          status: "invalid",
          remainingBalance: 0,
          appliedAmount: "",
        }));
      }
    } catch (err) {
      console.error(err);
      setGiftCardEntry((prev) => ({
        ...prev,
        status: "invalid",
        remainingBalance: 0,
        appliedAmount: "",
      }));
    }
  };

  const lockInGiftCardEntry = async () => {
    const appliedAmount = parseAmount(giftCardEntry.appliedAmount);
    const code = (giftCardEntry.code || "").trim();
    if (giftCardEntry.status !== "valid" || appliedAmount <= 0 || !code) {
      return null;
    }
    if (!ticketIdValue || isClosed || saving) return;
    setGiftCardEntryError("");
    try {
      const payment = await addTicketPayment(ticketIdValue, {
        payment_type: "payment_in",
        method: "gift_card",
        amount: appliedAmount,
        tip_amount: 0,
        reference: code,
        note: ticketNote || null,
        is_pending: true,
      });
      const newRow = {
        id: payment.id,
        code: payment.reference || code,
        remainingBalance: giftCardEntry.remainingBalance,
        appliedAmount: Number(payment.amount || appliedAmount),
        is_pending: true,
      };
      setFinalizedGiftCards((prev) => [...prev, newRow]);
      const refreshed = await getTicketById(ticketIdValue);
      setTicket(refreshed);
      await fetchSavedPayments();
      resetGiftCardEntry();
      const input = document.getElementById("gift-card-entry-code");
      if (input) {
        input.focus();
      }
      return newRow;
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to save gift card payment.");
      return null;
    }
  };

  const handleDeletePendingGiftCard = async (rowId) => {
    if (!ticketIdValue || isClosed || saving) return;
    setError("");
    try {
      await deleteTicketPayment(ticketIdValue, rowId);
      setFinalizedGiftCards((prev) => prev.filter((row) => row.id !== rowId));
      const refreshed = await getTicketById(ticketIdValue);
      setTicket(refreshed);
      await fetchSavedPayments();
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to remove gift card payment.");
    }
  };

  useEffect(() => {
    if (giftCardEntry.status !== "valid") return;
    const availableBalance = getAvailableBalanceForEntry();
    const maxAllowed = Math.max(0, Math.min(availableBalance, amountDueBeforeEntry));
    const current = parseAmount(giftCardEntry.appliedAmount);
    const clamped = Math.min(Math.max(current, 0), maxAllowed);
    if (current !== clamped) {
      setGiftCardEntry((prev) => ({
        ...prev,
        appliedAmount: clamped > 0 ? clamped.toFixed(2) : "",
      }));
    }
    if (current > maxAllowed) {
      setGiftCardEntryWarning(`Max ${formatMoney(maxAllowed)}`);
    } else {
      setGiftCardEntryWarning("");
    }
  }, [
    giftCardEntry.status,
    giftCardEntry.remainingBalance,
    giftCardEntry.appliedAmount,
    amountDueBeforeEntry,
    finalizedGiftCards,
  ]);

  useEffect(() => {
    if (!autoFilledKey) return;
    const remaining = getRemainingDueExcluding(autoFilledKey);
    const nextValue = remaining > 0 ? fmt2(remaining) : "";
    const currentValue = payments?.[autoFilledKey]?.amount ?? "";
    if (String(currentValue) !== String(nextValue)) {
      setPayments((prev) => ({
        ...prev,
        [autoFilledKey]: {
          ...(prev?.[autoFilledKey] || {}),
          amount: nextValue,
        },
      }));
    }
  }, [autoFilledKey, getRemainingDueExcluding, payments]);

  const handleSubmit = async () => {
    if (!ticketIdValue || isClosed) return;
    if (giftCardEntry.code && giftCardEntry.status !== "valid") {
      setGiftCardEntryError("Validate gift card before applying payment.");
      return;
    }
    let createdPendingRow = null;
    if (giftCardEntry.status === "valid" && entryGiftCardAmount > 0) {
      const code = (giftCardEntry.code || "").trim();
      if (!code) {
        setError("Gift card code is required.");
        return;
      }
      createdPendingRow = await lockInGiftCardEntry();
      if (!createdPendingRow) {
        return;
      }
    }

    const methodEntries = PAYMENT_METHODS.filter(
      (method) => method.key !== "gift_card"
    ).reduce((rows, method) => {
      const amount = parseMoney(payments[method.key]?.amount);
      const reference = payments[method.key]?.reference || "";
      if (amount <= 0) return rows;
      rows.push({
        payment_type: "payment_in",
        method: method.key,
        amount,
        tip_amount: 0,
        reference: reference.trim() ? reference.trim() : null,
        note: ticketNote || null,
        is_pending: false,
      });
      return rows;
    }, []);

    const giftCardRows = [
      ...finalizedGiftCards,
      ...(createdPendingRow ? [createdPendingRow] : []),
    ];
    const giftCardEntries = giftCardRows.reduce((rows, row) => {
      const amount = parseMoney(row?.appliedAmount);
      const code = (row?.code || "").trim();
      if (!code || amount <= 0) return rows;
      rows.push({
        payment_type: "payment_in",
        method: "gift_card",
        amount,
        tip_amount: 0,
        reference: code,
        note: ticketNote || null,
        is_pending: row?.is_pending ?? true,
      });
      return rows;
    }, []);

    const paymentEntries = [...methodEntries, ...giftCardEntries];
    const hasExistingPayments = loadedPayments.length > 0;
    if (amountDueCents > 0 && amountPaidCents <= 0 && !hasExistingPayments) {
      setError("Enter a payment amount.");
      return;
    }

    setSaving(true);
    setError("");
    setBanner("");
    try {
      const ticketStatusBeforeSave = ticket?.status || "";
      let emittedTicketCloseRefresh = false;
      const emitTicketCloseRefreshOnce = (nextTicket) => {
        if (
          !emittedTicketCloseRefresh &&
          didTicketJustClose(ticketStatusBeforeSave, nextTicket?.status)
        ) {
          emitScheduleRevenueRefresh();
          emittedTicketCloseRefresh = true;
        }
      };
      const noteTrimmed = (ticketNote || "").trim();
      const originalNote = (ticket?.notes || "").trim();
      if (noteTrimmed !== originalNote) {
        await updateTicket(ticketIdValue, { notes: ticketNote });
      }

      if (paymentEntries.length > 0) {
        await saveTicketPayments(ticketIdValue, { payments: paymentEntries });
      }

      let updated = await getTicketById(ticketIdValue);
      setTicket(updated);
      emitTicketCloseRefreshOnce(updated);
      await fetchSavedPayments();
      resetGiftCardEntry();

      const remainingBalance = parseMoney(updated?.balance_due || 0);
      if (remainingBalance <= 0 && updated?.status !== "closed") {
        updated = await closeTicket(ticketIdValue);
        setTicket(updated);
        emitTicketCloseRefreshOnce(updated);
      }

      if (updated?.status === "closed") {
        setBanner("Ticket closed successfully.");
        navigate(`/tickets/${ticketIdValue}`);
      } else {
        setBanner(
          `Payment saved. Remaining balance: ${formatMoney(
            updated?.balance_due || 0
          )}`
        );
      }
    } catch (err) {
      console.error(err);
      const detail =
        err?.response?.data?.detail || err?.response?.data?.message;
      if (detail?.code === "STAFF_NOT_AVAILABLE") {
        setError(
          "One or more technicians on this ticket are unavailable at the appointment time. Reassign services before closing the ticket."
        );
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError(detail?.message || err?.message || "Unable to save payment");
      }
    } finally {
      setSaving(false);
    }
  };

  const resolveStaffBaseTotal = (staffId) => {
    const row = serviceTotals.find((entry) => entry.staffId === staffId);
    if (row && row.amount > 0) return row.amount;
    return totalServiceAmount;
  };

  const handleTipEmployeeChange = (value) => {
    setTipEmployeeId(value);
    if (tipPercentInput) {
      const percent = parseAmount(tipPercentInput);
      const base = resolveStaffBaseTotal(Number(value || 0));
      if (percent > 0 && base > 0) {
        setTipAmountInput(((percent / 100) * base).toFixed(2));
      }
    }
  };

  const handleTipPercentChange = (value) => {
    setTipPercentInput(value);
    const percent = parseAmount(value);
    const base = resolveStaffBaseTotal(Number(tipEmployeeId || 0));
    if (percent > 0 && base > 0) {
      setTipAmountInput(((percent / 100) * base).toFixed(2));
    }
  };

  const handleAddTipRow = () => {
    setTipError("");
    if (!tipEmployeeId) {
      setTipError("Select an employee.");
      return;
    }
    const amountValue = parseAmount(tipAmountInput);
    if (amountValue <= 0) {
      setTipError("Amount can not be 0.00");
      return;
    }
    const staffId = Number(tipEmployeeId);
    const staff = staffOptions.find((entry) => entry.id === staffId);
    const staffName =
      staff?.display_name ||
      staff?.full_name ||
      staff?.name ||
      `Staff #${staffId}`;
    setTipRows((prev) => {
      const existing = prev.find((row) => row.staff_id === staffId);
      if (existing) {
        return prev.map((row) =>
          row.staff_id === staffId
            ? { ...row, amount: row.amount + amountValue }
            : row
        );
      }
      return [
        ...prev,
        { staff_id: staffId, staff_name: staffName, amount: amountValue },
      ];
    });
    setTipAmountInput("");
    setTipPercentInput("");
  };

  const handleDeleteTipRow = (staffId) => {
    setTipRows((prev) => prev.filter((row) => row.staff_id !== staffId));
  };

  const handleSaveTips = async () => {
    if (!ticketId || isClosed) return;
    setTipSaving(true);
    setTipError("");
    try {
      const payload = {
        tips: tipRows.map((row) => ({
          staff_id: row.staff_id,
          amount: Number(row.amount || 0),
        })),
      };
      const updated = await setTicketTips(ticketId, payload);
      setTicket(updated);
      setShowTipModal(false);
    } catch (err) {
      console.error(err);
      setTipError(err?.message || "Unable to save tips");
    } finally {
      setTipSaving(false);
    }
  };

  const handleCloseTipModal = () => {
    if (tipSaving) return;
    setShowTipModal(false);
  };

  const handleRedeemRewards = async () => {
    if (!ticketIdValue || !ticket?.client_id) return;
    if (!rewardsEnabled) {
      setRedeemError("Rewards are disabled for this salon.");
      return;
    }
    const points = Math.floor(Number(redeemPoints || 0));
    if (!points || points <= 0) {
      setRedeemError("Enter points to redeem.");
      return;
    }
    setRedeemSaving(true);
    setRedeemError("");
    setRedeemNotice("");
    try {
      await redeemTicketRewards(ticketIdValue, { points_to_redeem: points });
      setRedeemPoints("");
      setRedeemNotice("Rewards applied.");
      await fetchTicket();
      await fetchSavedPayments();
      await loadRewardsData();
    } catch (err) {
      setRedeemError(
        err?.response?.data?.detail || "Unable to apply rewards."
      );
    } finally {
      setRedeemSaving(false);
    }
  };

  const handleDeleteRewardsPayment = async (paymentId) => {
    if (!paymentId || !ticketIdValue) return;
    setRedeemSaving(true);
    setRedeemError("");
    setRedeemNotice("");
    try {
      await deleteTicketPayment(ticketIdValue, paymentId);
      await fetchTicket();
      await fetchSavedPayments();
      await loadRewardsData();
    } catch (err) {
      setRedeemError(
        err?.response?.data?.detail || "Unable to remove rewards payment."
      );
    } finally {
      setRedeemSaving(false);
    }
  };

  if (loading && !ticket) {
    return (
      <div className="ticketPaymentPageShell min-h-screen bg-slate-50/60 py-6">
        <div className="ticketPaymentPageWide ticket-payment-page-compact">
          <div className="max-w-3xl px-4 text-base md:text-[17px] text-slate-600">
            Loading ticket...
          </div>
        </div>
      </div>
    );
  }

  if (!ticket && error) {
    return (
      <div className="ticketPaymentPageShell min-h-screen bg-slate-50/60 py-6">
        <div className="ticketPaymentPageWide ticket-payment-page-compact">
          <div className="max-w-3xl px-4">
            <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
            <button
              type="button"
              className="text-sm text-emerald-600 underline"
              onClick={() => navigate("/tickets")}
            >
              Back to Tickets
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) return null;

  return (
    <div className="ticketPaymentPageShell min-h-screen bg-slate-50/60 py-6">
      <div className="ticketPaymentPageWide ticket-payment-page-compact">
        <SBINPageCard
          title="Sales Ticket: Collect Payment"
          subtitle={`Client: ${clientName}`}
          actions={headerActions}
        >
          <div className="space-y-4">
            {error && (
              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            {banner && !error && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
                {banner}
              </div>
            )}
            {isClosed && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-700">
                Ticket is closed.
              </div>
            )}

      <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-5">
        <div className="space-y-3">
          <div className="grid grid-cols-2 text-sm md:text-base font-semibold uppercase tracking-wide text-slate-600">
            <span>Payment Method</span>
            <span className="text-right">Amount</span>
          </div>
          <div className="space-y-2">
            {PAYMENT_METHODS.map((method) => {
              const methodKey = String(method.key || "").toLowerCase();

              const prev = previousByMethod?.[methodKey];
              const prevAmount = prev ? Number(prev.amount || 0) : 0;
              const isClosed = (ticket?.status || "").toLowerCase() === "closed";
              const showPrev = !isClosed && prev && prevAmount > 0;

              const currentAmountValue = payments?.[methodKey]?.amount ?? "";
              const amountPlaceholder =
                !currentAmountValue && showPrev
                  ? `Prev: ${prevAmount.toFixed(2)}`
                  : "0.00";

              if (methodKey === "gift_card") {
                const entryBalanceToShow = getAvailableBalanceForCode(
                  giftCardEntry.code,
                  giftCardEntry.remainingBalance
                );
                const entryStatusText =
                  giftCardEntry.status === "valid"
                    ? `Balance ${entryBalanceToShow.toFixed(2)}`
                    : giftCardEntry.status === "invalid"
                    ? "Not Valid"
                    : "";
                return (
                  <div key={method.key} className="space-y-2">
                    {finalizedGiftCards.map((row) => (
                      <div
                        key={row.id}
                        className="grid grid-cols-2 items-center gap-3"
                      >
                        <div className="flex flex-wrap items-center gap-2 text-base md:text-lg text-slate-800">
                          <span className="font-semibold">{method.label}</span>
                          <input
                            type="text"
                            value={row.code}
                            readOnly
                            className="h-11 md:h-12 border border-slate-300 rounded-xl px-3 py-2 text-base md:text-lg w-36 bg-slate-50"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              row.is_pending
                                ? handleDeletePendingGiftCard(row.id)
                                : null
                            }
                            disabled={isClosed || saving || !row.is_pending}
                            className="text-rose-600 disabled:text-slate-400"
                            aria-label="Remove gift card"
                          >
                            <svg
                              viewBox="0 0 24 24"
                              className="h-5 w-5"
                              fill="currentColor"
                            >
                              <path d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v9h-2V9zm4 0h2v9h-2V9zM7 9h2v9H7V9z" />
                            </svg>
                          </button>
                        </div>
                        <div className="flex justify-end">
                          <input
                            type="number"
                            step="0.01"
                            value={Number(row.appliedAmount || 0).toFixed(2)}
                            readOnly
                            className="h-11 md:h-12 border border-slate-300 rounded-xl px-3 py-2 text-base md:text-lg w-28 md:w-32 text-right bg-slate-50"
                          />
                        </div>
                      </div>
                    ))}
                    {!isClosed && previousGiftCards.length > 0 && (
                      <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Previous Gift Card Payments
                        </div>
                        <div className="mt-1 space-y-1 text-sm text-slate-600">
                          {previousGiftCards.map((gc, idx) => (
                            <div
                              key={`${gc.code}-${idx}`}
                              className="flex justify-between"
                            >
                              <span>{gc.code || "Gift Card"}</span>
                              <span>{formatMoney(gc.amount)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 items-center gap-3">
                      <div className="flex flex-wrap items-center gap-2 text-base md:text-lg text-slate-800">
                        <span className="font-semibold">{method.label}</span>
                        <input
                          id="gift-card-entry-code"
                          type="text"
                          value={giftCardEntry.code}
                          onChange={(e) =>
                            handleGiftCardEntryChange("code", e.target.value)
                          }
                          className="h-11 md:h-12 border border-slate-300 rounded-xl px-3 py-2 text-base md:text-lg w-36"
                          placeholder={method.referenceLabel}
                          disabled={isClosed || saving}
                        />
                        <button
                          type="button"
                          onClick={handleValidateGiftCard}
                          disabled={
                            isClosed ||
                            saving ||
                            giftCardEntry.status === "loading"
                          }
                          className="text-sm text-sky-600 underline disabled:text-slate-400"
                        >
                          Validate
                        </button>
                        {entryStatusText && (
                          <span className="text-sm font-semibold text-red-600">
                            {entryStatusText}
                          </span>
                        )}
                        {giftCardEntryWarning && (
                          <span className="text-xs text-amber-600">
                            {giftCardEntryWarning}
                          </span>
                        )}
                        {giftCardEntryError && (
                          <span className="text-xs text-rose-600">
                            {giftCardEntryError}
                          </span>
                        )}
                      </div>
                      <div className="flex justify-end">
                        <input
                          type="number"
                          step="0.01"
                          value={giftCardEntry.appliedAmount}
                          onChange={(e) =>
                            handleGiftCardEntryChange(
                              "appliedAmount",
                              e.target.value
                            )
                          }
                          onBlur={lockInGiftCardEntry}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              lockInGiftCardEntry();
                            }
                          }}
                          className="h-11 md:h-12 border border-slate-300 rounded-xl px-3 py-2 text-base md:text-lg w-28 md:w-32 text-right"
                          placeholder="0.00"
                          disabled={isClosed || saving}
                        />
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={method.key}
                  className="grid grid-cols-2 items-center gap-3"
                >
                  <div className="flex items-center gap-2 text-base md:text-lg text-slate-800">
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-semibold ${
                          method.hover
                            ? "cursor-help underline decoration-dotted underline-offset-2"
                            : ""
                        }`}
                        title={method.hover || undefined}
                      >
                        {method.label}
                      </span>
                      {showPrev && (
                        <span className="ml-2 text-xs text-slate-400">
                          Previous: {formatMoney(prevAmount)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <input
                      type="number"
                      step="0.01"
                      value={currentAmountValue}
                      ref={(el) => {
                        amountInputRefs.current[methodKey] = el;
                      }}
                      onFocus={() => handleAmountFocus(methodKey)}
                      onClick={() => handleAmountFocus(methodKey)}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPayments((prevState) => ({
                          ...prevState,
                          [methodKey]: {
                            ...(prevState?.[methodKey] || {}),
                            amount: v,
                          },
                        }));
                        if (autoFilledKey === methodKey) {
                          setAutoFilledKey("");
                        }
                      }}
                      className="h-11 md:h-12 border border-slate-300 rounded-xl px-3 py-2 text-base md:text-lg w-28 md:w-32 text-right"
                      placeholder={amountPlaceholder}
                      disabled={isClosed || saving}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {ticket?.client_id && (
          <div className="border-t border-slate-200 pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Rewards
              </span>
              {rewardsLoading && (
                <span className="text-xs text-slate-500">Loading...</span>
              )}
            </div>
            {rewardsError && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {rewardsError}
              </div>
            )}
            {!rewardsError && !rewardsLoading && !rewardsEnabled && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                Rewards are currently disabled for this salon.
              </div>
            )}
            {!rewardsError && rewardsEnabled && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-700">
                  <span>
                    Balance:{" "}
                    <span className="font-semibold text-slate-900">
                      {rewardsBalancePoints} pts
                    </span>{" "}
                    ({formatMoney(rewardsBalanceValue)})
                  </span>
                  {rewardPayments.length > 0 && (
                    <span className="text-xs text-slate-500">
                      {rewardPayments.length} rewards payment
                      {rewardPayments.length === 1 ? "" : "s"} applied
                    </span>
                  )}
                </div>
                {rewardPayments.length > 0 && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 space-y-2 text-sm text-slate-700">
                    {rewardPayments.map((row) => (
                      <div
                        key={row.id}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="font-medium">Rewards Payment</span>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-800">
                            {formatMoney(row.amount)}
                          </span>
                          {row?.is_pending && !isClosed && (
                            <button
                              type="button"
                              onClick={() => handleDeleteRewardsPayment(row.id)}
                              disabled={redeemSaving || saving}
                              className="text-xs text-rose-600 hover:text-rose-700 disabled:text-slate-400"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-[200px_1fr_auto] items-center gap-3">
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={redeemPoints}
                    onChange={(e) => {
                      setRedeemPoints(e.target.value);
                      setRedeemError("");
                      setRedeemNotice("");
                    }}
                    className="h-11 md:h-12 border border-slate-300 rounded-xl px-3 py-2 text-base md:text-lg w-full text-right"
                    placeholder="Points to redeem"
                    disabled={isClosed || saving || redeemSaving}
                  />
                  <div className="text-sm text-slate-600">
                    ≈ {formatMoney(redeemPointsValue)}
                    <div className="text-xs text-slate-500">
                      Min {rewardsSettings?.min_redeem_points ?? 0} pts • Max{" "}
                      {rewardsSettings?.max_redeem_percent_of_ticket ?? 100}% of
                      ticket
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleRedeemRewards}
                    disabled={isClosed || saving || redeemSaving}
                    className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                  >
                    {redeemSaving ? "Applying..." : "Apply Rewards"}
                  </button>
                </div>
                {redeemNotice && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                    {redeemNotice}
                  </div>
                )}
                {redeemError && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {redeemError}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="border-t border-slate-200 pt-4 space-y-3">
          <div className="grid grid-cols-2 text-sm md:text-base font-semibold uppercase tracking-wide text-slate-600">
            <span>Totals</span>
            <span className="text-right">Amount</span>
          </div>
          <div className="space-y-2 text-base text-slate-700">
            <div className="flex justify-between">
              <span className="text-base md:text-lg text-slate-800">
                Items, Fees, & Taxes
              </span>
              <span className="text-base md:text-lg font-medium text-slate-900">
                {formatMoney(ticket.grand_total)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-base md:text-lg text-slate-800">Tips</span>
                <button
                  type="button"
                  className="text-sm text-sky-600 underline disabled:text-slate-400"
                  onClick={() => setShowTipModal(true)}
                  disabled={isClosed || saving}
                >
                  Manage
                </button>
              </div>
              <span className="text-base md:text-lg font-medium text-slate-900">
                {formatMoney(ticket.total_tips || 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-base md:text-lg text-slate-800">Amount Paid</span>
              <span className="text-lg md:text-xl font-semibold text-emerald-600">
                {formatMoney(amountPaidDisplay)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-base md:text-lg text-slate-800">Amount Due</span>
              <span className="text-lg md:text-xl font-semibold text-red-600">
                {formatMoney(amountDueDisplay)}
              </span>
            </div>
            <div className="flex justify-between text-sm text-slate-500">
              <span>Current balance due</span>
              <span>{formatMoney(ticket.balance_due)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-base md:text-lg text-slate-800">Change Due</span>
              <span className="text-base md:text-lg font-medium text-slate-900">
                {formatMoney(changeDueDisplay)}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-base md:text-lg font-semibold text-slate-800">
            Ticket Note
          </label>
          <textarea
            value={ticketNote}
            onChange={(e) => setTicketNote(e.target.value)}
            rows={3}
            className="w-full border border-slate-300 rounded-xl px-4 py-3 text-base md:text-lg min-h-[110px] focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            placeholder="Add a note about this payment..."
            disabled={isClosed || saving}
          ></textarea>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isClosed || saving}
            className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-full px-8 text-base md:text-lg disabled:opacity-60"
          >
            {saving ? "Saving..." : "Close Out Ticket"}
          </button>
        </div>
      </section>

      {showTipModal && (
        <div className="fixed inset-0 z-[10040] flex items-center justify-center bg-black/60 px-4 py-6">
          <div
            className={`w-full max-w-2xl rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden ${modalBase}`}
          >
            <div className="flex items-center justify-between bg-rose-500 px-6 py-4">
              <h2 className={`${modalTitle} text-white`}>Manage Tips</h2>
              <ModalCloseX
                onClick={handleCloseTipModal}
                disabled={tipSaving}
              />
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex flex-col gap-1 md:col-span-2">
                  <label className={labelCls}>Employee</label>
                  <select
                    value={tipEmployeeId}
                    onChange={(e) => handleTipEmployeeChange(e.target.value)}
                    className={`w-full ${fieldCls}`}
                    disabled={staffLoading || tipSaving || staffOptions.length === 0}
                  >
                    {staffOptions.length === 0 ? (
                      <option value="">No staff</option>
                    ) : (
                      staffOptions.map((staff) => (
                        <option key={staff.id} value={staff.id}>
                          {staff.display_name || staff.full_name || staff.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className={labelCls}>Amount</label>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={tipAmountInput}
                      onChange={(e) => setTipAmountInput(e.target.value)}
                      className={`w-full ${fieldCls} text-right`}
                      disabled={tipSaving}
                    />
                  </div>
                </div>
                <div className="hidden md:flex items-end justify-center pb-3 text-xs md:text-sm font-semibold text-slate-400">
                  OR
                </div>
                <div className="flex flex-col gap-1">
                  <label className={labelCls}>Percent</label>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="number"
                      step="0.01"
                      value={tipPercentInput}
                      onChange={(e) => handleTipPercentChange(e.target.value)}
                      className={`w-32 ${fieldCls} text-right`}
                      disabled={tipSaving}
                    />
                    <span className="text-slate-500">
                      % of {formatMoney(selectedStaffTotal || 0)}.
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleAddTipRow}
                  className={`${btnCls} border border-slate-300 font-semibold text-slate-700 transition hover:border-slate-400`}
                  disabled={tipSaving}
                >
                  Add
                </button>
                {tipError && (
                  <div className="text-sm font-semibold text-rose-600">
                    {tipError}
                  </div>
                )}
              </div>
              {tipRows.length > 0 && (
                <div className="rounded-2xl border border-slate-200">
                  <div className="grid grid-cols-[1fr,140px,40px] gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2 text-sm md:text-base font-semibold text-slate-700">
                    <span>Employee</span>
                    <span className="text-right">Amount</span>
                    <span></span>
                  </div>
                  <div className="space-y-2 px-4 py-3 text-sm md:text-base text-slate-700">
                    {tipRows.map((row) => (
                      <div
                        key={row.staff_id}
                        className="grid grid-cols-[1fr,140px,40px] items-center gap-2"
                      >
                        <span>{row.staff_name}</span>
                        <span className="text-right">
                          {formatMoney(row.amount)}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleDeleteTipRow(row.staff_id)}
                          className="flex items-center justify-center text-rose-600"
                          aria-label="Remove tip"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            className="h-5 w-5"
                            fill="currentColor"
                          >
                            <path d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v9h-2V9zm4 0h2v9h-2V9zM7 9h2v9H7V9z" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="rounded-2xl border border-slate-200 p-4 md:p-5 text-base md:text-lg">
                <div className={labelCls}>Service totals by technician</div>
                <div className="mt-2 space-y-1 text-slate-700">
                  {serviceTotals.length === 0 ? (
                    <div>No service items.</div>
                  ) : (
                    serviceTotals.map((row) => (
                      <div key={row.staffId}>
                        {row.staffName} - {formatMoney(row.amount)}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                onClick={handleCloseTipModal}
                className={`${btnCls} border border-slate-300 font-semibold text-slate-700 transition hover:border-slate-400`}
                disabled={tipSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveTips}
                disabled={tipSaving}
                className={`${btnCls} font-semibold text-white transition bg-rose-500 hover:bg-rose-600 disabled:opacity-60 disabled:cursor-not-allowed`}
              >
                {tipSaving ? "Saving..." : "Done"}
              </button>
            </div>
          </div>
        </div>
      )}
          </div>
        </SBINPageCard>
      </div>
    </div>
  );
}
